import { useEffect, useCallback, useRef } from 'react';

import { usePersonalisationContext } from '../../context/PersonalisationContext';
import { baseProductConfigurationStepId } from '../../utilities/strings';
import { isStepRequired } from '../../utilities/stepValidation';
import { isPairedJourney } from '../../utilities/isPairedJourney';

import styles from './personalisation-modal.module.css';

/**
 * A PAIRED step's title, "Stone n of m": m is its stones and n the one the customer is on, one
 * past those already chosen and held at m once every stone is in (Figma V2 screens 2–5).
 *
 * @param {Object} options - Options object.
 * @param {Object} options.step - The PAIRED step.
 * @param {Map<string, Object>|null|undefined} options.personalisationSlots - Every slot, by id.
 * @param {{ slots?: Array<{ id: string, selected: unknown }> }|null|undefined} options.selections - The customer's selections.
 * @returns {string|null} The title, or null for a step with no stones.
 */
function derivePairedStepTitle({ step, personalisationSlots, selections }) {
  const stoneSlotIds = (step.slots || []).filter(
    (slotId) => personalisationSlots?.get(slotId)?.slotType === 'COMPONENT'
  );
  if (stoneSlotIds.length === 0) return null;

  // A stone counts once its slot holds a variant id, the same rule the step's rows use.
  const chosenCount = stoneSlotIds.filter((slotId) => {
    const selected = selections?.slots?.find((selection) => selection.id === slotId)?.selected;
    return typeof selected === 'string' && selected !== '';
  }).length;

  return `Stone ${Math.min(chosenCount + 1, stoneSlotIds.length)} of ${stoneSlotIds.length}`;
}

/**
 * The modal header's title for the current step.
 *
 * An optional step's heading gets a forced " (optional)" suffix, except in a Story of Us (PAIRED)
 * journey, whose Figma sets each title in full ("Engraving", "Keepsake Card (Optional)"), so the
 * configured heading is shown exactly as written. The PAIRED step itself counts its stones.
 *
 * @param {Object} options - Options object.
 * @param {Map<string, Object>|null|undefined} options.personalisationSteps - Every step, by id.
 * @param {Map<string, Object>|null|undefined} options.personalisationSlots - Every slot, by id.
 * @param {{ slots?: Array<{ id: string, selected: unknown }> }|null|undefined} [options.selections] - The customer's selections.
 * @param {string|null|undefined} options.currentStepId - The current step's id.
 * @returns {string} The title.
 */
export function deriveHeaderTitle({ personalisationSteps, personalisationSlots, selections, currentStepId }) {
  if (!personalisationSteps || !currentStepId) return 'Add Personalisation';

  const currentStepObj = personalisationSteps.get(currentStepId);

  // For base config step, use uiStepHeading from metaobject
  if (currentStepId === baseProductConfigurationStepId && currentStepObj?.uiStepHeading) {
    return currentStepObj.uiStepHeading;
  }

  if (currentStepObj?.slotMode === 'PAIRED') {
    const pairedStepTitle = derivePairedStepTitle({ step: currentStepObj, personalisationSlots, selections });
    if (pairedStepTitle) return pairedStepTitle;
  }

  if (!currentStepObj || !currentStepObj.uiHeading) return 'Add Personalisation';

  if (isPairedJourney(personalisationSteps)) return currentStepObj.uiHeading;

  // Process the title for optional steps
  const rawHeading = currentStepObj.uiHeading.replace('(optional)', '').trim();

  // Check if step is completely optional (not required)
  const stepIsRequired = isStepRequired({
    step: currentStepObj,
    personalisationSlots: personalisationSlots || new Map()
  });

  const isCompletelyOptional = !stepIsRequired;

  return isCompletelyOptional ? `${rawHeading} (optional)` : rawHeading;
}

/**
 * Custom hook for PersonalisationModal logic and className selection.
 * @param {Object} options - Options object.
 * @param {boolean} options.isOpen - Whether the modal is open.
 * @param {function} options.onClose - Callback for closing the modal.
 * @returns {Object} View model for the modal component.
 */
export function useViewModel({ isOpen, onClose }) {
  const {
    personalisation,
    personalisationSteps,
    personalisationSlots,
    selections,
    setCurrentStepId,
    currentStepId,
    isResetting,
    resetJourney,
    isAddingToCart,
    isRetrying,
    stepSubview
  } = usePersonalisationContext();

  // A full-screen view a step has opened over its content (the PAIRED stone picker).
  const isSubviewOpen = Boolean(stepSubview);

  // Handle personalisation modal dialog
  /** @type {React.RefObject<HTMLDialogElement | null>} */
  const dialogModalRef = useRef(null);

  // Handle content scroll position
  /** @type {React.RefObject<HTMLDivElement | null>} */
  const contentRef = useRef(null);

  useEffect(() => {
    const dialog = dialogModalRef.current;
    if (!dialog) return;

    if (isOpen && !dialog.open) {
      dialog.showModal();
      setTimeout(() => {
        dialog.setAttribute('data-open', 'true');
      }, 10);
    } else if (!isOpen && dialog.open && !isAddingToCart) {
      // Only close if not adding to cart
      dialog.setAttribute('data-open', 'false');
      setTimeout(() => {
        dialog.close();
      }, 400);
    }
  }, [isOpen, isAddingToCart]);

  useEffect(() => {
    const dialog = dialogModalRef.current;
    if (!dialog) return;

    const handleNativeClose = () => {
      if (!isOpen) return;
      if (isOpen && !isAddingToCart) {
        // Only close if not adding to cart
        dialog.setAttribute('data-open', 'false');
        setTimeout(() => {
          onClose?.();
        }, 400);
      }
    };

    dialog.addEventListener('close', handleNativeClose);
    return () => {
      dialog.removeEventListener('close', handleNativeClose);
    };
  }, [isOpen, onClose, isAddingToCart]);

  // While a step's sub-view is open, the dialog's close request (Escape, or a light dismiss) closes
  // that view rather than the journey. The picker handles Escape itself while focus is inside it;
  // this catches it when focus is elsewhere, e.g. on the dialog after a tap on blank space.
  useEffect(() => {
    const dialog = dialogModalRef.current;
    if (!dialog || !stepSubview) return undefined;

    const handleCancel = (/** @type {Event} */ event) => {
      event.preventDefault();
      stepSubview.onCancel();
    };

    dialog.addEventListener('cancel', handleCancel);
    return () => {
      dialog.removeEventListener('cancel', handleCancel);
    };
  }, [stepSubview]);

  const handleClose = () => {
    if (onClose && !isAddingToCart) {
      // Only allow close if not adding to cart
      onClose();
    }
  };

  // Compute total steps and current step index
  const stepIds = Array.from(personalisationSteps.keys());
  const totalSteps = personalisationSteps?.size || 0;
  const currentStep = stepIds.indexOf(currentStepId);

  // Dynamic header title for modal. Header is configured for each step.
  const headerTitle = deriveHeaderTitle({ personalisationSteps, personalisationSlots, selections, currentStepId });

  const headerResetText = 'Reset';

  // Per-Personalisation header design. personalisationData already normalises it; null (before
  // the data loads) is V1 too.
  const headerStyle = personalisation?.headerStyle === 'V2' ? 'V2' : 'V1';

  /**
   * Scrolls the content area to the top.
   */
  const scrollToTop = useCallback(() => {
    if (contentRef.current) {
      contentRef.current.scrollTo(0, 0);
    }
  }, []);

  // A sub-view is a new screen, so it starts at the top like a new step does.
  useEffect(() => {
    if (isSubviewOpen) scrollToTop();
  }, [isSubviewOpen, scrollToTop]);

  const handleNext = useCallback(() => {
    setCurrentStepId((prevId) => {
      const ids = Array.from(personalisationSteps.keys());
      const currentIndex = ids.indexOf(prevId);
      const nextId = ids.at(currentIndex + 1);
      return nextId || prevId;
    });
    // Scroll to top when navigating to next step.
    scrollToTop();
  }, [personalisationSteps, setCurrentStepId, scrollToTop]);

  const handlePrev = useCallback(() => {
    setCurrentStepId((prevId) => {
      const ids = Array.from(personalisationSteps.keys());
      const currentIndex = ids.indexOf(prevId);
      const newPrevId = ids.at(currentIndex - 1);
      return newPrevId || prevId;
    });
    // Scroll to top when navigating to previous step.
    scrollToTop();
  }, [personalisationSteps, setCurrentStepId, scrollToTop]);

  // The V2 header's Back. handlePrev wraps from the first step to the last, so on the first
  // step Back leaves the journey instead, the same as the close button.
  const handleBack = () => {
    if (currentStep > 0) {
      handlePrev();
    } else {
      handleClose();
    }
  };

  // A sub-view fills the content area and its own action bar sits flush with the bottom, so the
  // content area becomes a column it can grow into, with no bottom padding, while one is open.
  const contentClassName =
    styles['personalisation__content'] + (isSubviewOpen ? ' ' + styles['personalisation__content--subview'] : '');

  return {
    dialogModalRef,
    contentRef,
    contentClassName,
    isSubviewOpen,
    totalSteps,
    currentStep,
    handleClose,
    handleNext,
    handlePrev,
    handleBack,
    handleReset: resetJourney,
    isResetting,
    isAddingToCart,
    isRetrying,
    // A sub-view names itself ("Stone 1 of 5") in place of the step heading, in both header styles.
    headerTitle: stepSubview ? stepSubview.title : headerTitle,
    headerResetText,
    headerStyle
  };
}
